/**
 * What each door swing is, for every view that draws one: how it opens, the
 * side it works from, and the leaves a hinged door swings open. The plan
 * glyphs, the 3D fixtures and a room's outward reach all read this one table,
 * so a new hinged swing is a schema value plus a row here.
 */
import { type DoorSwing, isKnownDoorSwing } from './schemas';

/** How a door opens, which decides how every view draws it. */
export type DoorKind = 'hinged' | 'pocket' | 'bifold' | 'bypass' | 'garage' | 'slidingGlass';

/** The side a one-sided door works from, looking at its wall from inside the room. */
export type DoorHand = 'left' | 'right';

/**
 * One hinged leaf. `alongSign` is the way the closed leaf runs from its
 * hinge: 1 hinges it on the opening's start (left) jamb, -1 on its end
 * (right) jamb. `intoSign` is the way it swings open: 1 into the room, -1
 * out of it. `widthShare` is its length as a share of the opening's width.
 */
export interface DoorLeaf {
  alongSign: 1 | -1;
  intoSign: 1 | -1;
  widthShare: number;
}

/**
 * A door swing's shape: its kind; its hand, for a door that has one (the
 * hinge of a single swing door, the half a pocket door's panel covers, the
 * sliding panel of a sliding glass door); and its hinged leaves, none for a
 * door that doesn't swing.
 */
export interface DoorSwingShape {
  kind: DoorKind;
  hand?: DoorHand;
  leaves: DoorLeaf[];
}

const leaf = (alongSign: 1 | -1, opens: 'in' | 'out', widthShare: number): DoorLeaf => ({ alongSign, intoSign: opens === 'in' ? 1 : -1, widthShare });
const single = (hand: DoorHand, opens: 'in' | 'out'): DoorSwingShape => ({ kind: 'hinged', hand, leaves: [leaf(hand === 'left' ? 1 : -1, opens, 1)] });
const pair = (opens: 'in' | 'out'): DoorSwingShape => ({ kind: 'hinged', leaves: [leaf(1, opens, 0.5), leaf(-1, opens, 0.5)] });
const leafless = (kind: DoorKind, hand?: DoorHand): DoorSwingShape => ({ kind, hand, leaves: [] });

const DOOR_SWING_SHAPES: Record<DoorSwing, DoorSwingShape> = {
  inLeft: single('left', 'in'),
  inRight: single('right', 'in'),
  outLeft: single('left', 'out'),
  outRight: single('right', 'out'),
  pairIn: pair('in'),
  pairOut: pair('out'),
  pocketLeft: leafless('pocket', 'left'),
  pocketRight: leafless('pocket', 'right'),
  bifold: leafless('bifold'),
  bypass: leafless('bypass'),
  garage: leafless('garage'),
  slideGlassLeft: leafless('slidingGlass', 'left'),
  slideGlassRight: leafless('slidingGlass', 'right'),
};

/** A door of a swing a newer version added: a plain door with no leaf or arc. */
const UNKNOWN_SWING_SHAPE: DoorSwingShape = { kind: 'hinged', leaves: [] };

/**
 * The shape of a door of `swing`. A door with no swing is an in-left swing
 * door; one of a swing this version doesn't know draws as a plain door with
 * no leaf or arc.
 */
export const doorSwingShape = (swing: string = 'inLeft'): DoorSwingShape => (isKnownDoorSwing(swing) ? DOOR_SWING_SHAPES[swing] : UNKNOWN_SWING_SHAPE);
